import { useEffect, useMemo, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'

export type WorkspaceWalletBalance = {
  trialCredits: number
  earnedCredits: number
  availableCredits: number
  reservedCredits: number
  lifetimeSpentCredits: number
  isLoading: boolean
}

const EMPTY_WORKSPACE_WALLET: WorkspaceWalletBalance = {
  trialCredits: 0,
  earnedCredits: 0,
  availableCredits: 0,
  reservedCredits: 0,
  lifetimeSpentCredits: 0,
  isLoading: false,
}

const asCredits = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0

export const usePersonalWalletCredits = (uid: string | null | undefined) => {
  const [subscribedState, setSubscribedState] = useState<{ uid: string; credits: number } | null>(null)

  const credits = (uid && subscribedState?.uid === uid) ? subscribedState.credits : 0
  const isLoading = Boolean(uid && subscribedState?.uid !== uid)

  useEffect(() => {
    if (!uid) {
      return undefined
    }

    const currentUid = uid
    return onSnapshot(
      doc(firestoreDb, 'users', currentUid, 'wallet', 'personal'),
      (snapshot) => {
        setSubscribedState({ uid: currentUid, credits: asCredits(snapshot.data()?.availableCredits) })
      },
      (error) => {
        console.warn('[Credits] personal wallet listener failed', error)
        setSubscribedState({ uid: currentUid, credits: 0 })
      },
    )
  }, [uid])

  return { credits, isLoading }
}

export const useWorkspaceWalletBalance = (
  workspaceId: string | null | undefined,
  fallbackCredits = 0,
  _isFreeTier = false,
) => {
  void _isFreeTier
  const fallbackAvailable = asCredits(fallbackCredits)
  const [subscribedWalletState, setSubscribedWalletState] = useState<{
    workspaceId: string
    wallet: WorkspaceWalletBalance
  } | null>(null)

  const wallet = useMemo<WorkspaceWalletBalance>(() => {
    if (!workspaceId) {
      return {
        ...EMPTY_WORKSPACE_WALLET,
        availableCredits: fallbackAvailable,
        earnedCredits: fallbackAvailable,
        isLoading: false,
      }
    }
    if (subscribedWalletState?.workspaceId === workspaceId) {
      return subscribedWalletState.wallet
    }
    return {
      ...EMPTY_WORKSPACE_WALLET,
      availableCredits: fallbackAvailable,
      earnedCredits: fallbackAvailable,
      isLoading: true,
    }
  }, [workspaceId, fallbackAvailable, subscribedWalletState])

  useEffect(() => {
    if (!workspaceId) {
      return undefined
    }

    const currentWorkspaceId = workspaceId
    return onSnapshot(
      doc(firestoreDb, 'workspace_wallet', currentWorkspaceId),
      (snapshot) => {
        const data = snapshot.data()
        if (!snapshot.exists() || !data) {
          setSubscribedWalletState({
            workspaceId: currentWorkspaceId,
            wallet: {
              ...EMPTY_WORKSPACE_WALLET,
              trialCredits: 0,
              earnedCredits: fallbackAvailable,
              availableCredits: fallbackAvailable,
              isLoading: false,
            },
          })
          return
        }

        const trialCredits = asCredits(data.trialCredits)
        const earnedCredits = asCredits(data.earnedCredits ?? data.availableCredits)
        setSubscribedWalletState({
          workspaceId: currentWorkspaceId,
          wallet: {
            trialCredits,
            earnedCredits,
            availableCredits: asCredits(data.availableCredits ?? trialCredits + earnedCredits),
            reservedCredits: asCredits(data.reservedCredits),
            lifetimeSpentCredits: asCredits(data.lifetimeSpentCredits),
            isLoading: false,
          },
        })
      },
      (error) => {
        console.warn('[Credits] workspace wallet listener failed', error)
        setSubscribedWalletState({
          workspaceId: currentWorkspaceId,
          wallet: {
            ...EMPTY_WORKSPACE_WALLET,
            availableCredits: fallbackAvailable,
            earnedCredits: fallbackAvailable,
            isLoading: false,
          },
        })
      },
    )
  }, [fallbackAvailable, workspaceId])

  return wallet
}

export const useSpendableCredits = (
  uid: string | null | undefined,
  workspaceId: string | null | undefined,
  fallbackWorkspaceCredits = 0,
  isFreeTier = false,
) => {
  const personalWallet = usePersonalWalletCredits(uid)
  const workspaceWallet = useWorkspaceWalletBalance(workspaceId, fallbackWorkspaceCredits, isFreeTier)

  return useMemo(() => ({
    personalCredits: personalWallet.credits,
    workspaceWallet,
    spendableCredits: workspaceWallet.availableCredits + personalWallet.credits,
    isLoading: personalWallet.isLoading || workspaceWallet.isLoading,
  }), [personalWallet.credits, personalWallet.isLoading, workspaceWallet])
}
